import {
  checkSkillUpdates,
  configureSkillUpdates,
  applySkillUpdate,
  updateSkillSettings,
} from "./skillUpdates";
import { defineMutators } from "@rocicorp/zero";
import { addCustomDomain } from "./addCustomDomain";
import { removeCustomDomain } from "./removeCustomDomain";
import { updateCustomDomain } from "./updateCustomDomain";
import { associateBranchChatWithConnectedApp } from "./associateBranchChatWithConnectedApp";
import { associatePersonalChatWithConnectedApp } from "./associatePersonalChatWithConnectedApp";
import { associateStepWithConnectedApp } from "./associateStepWithConnectedApp";
import { closePersonalChat } from "./closePersonalChat";
import { completeProfile } from "./completeProfile";
import { configureGitSync } from "./configureGitSync";
import { createApiKey } from "./createApiKey";
import { createAuditLogDestination } from "./createAuditLogDestination";
import { createBranch } from "./createBranch";
import { createConnectedApp } from "./createConnectedApp";
import { createEgressRoute } from "./createEgressRoute";
import { createFeatureFlag } from "./createFeatureFlag";
import { createEgressRule } from "./createEgressRule";
import { createNetwork } from "./createNetwork";
import { createPersonalChat } from "./createPersonalChat";
import { createRole } from "./createRole";
import { createServiceAccount } from "./createServiceAccount";
import { createServiceAccountFederation } from "./createServiceAccountFederation";
import { createServiceAccountSession } from "./createServiceAccountSession";
import { createUserApiKey } from "./createUserApiKey";
import { createSpace } from "./createSpace";
import { createSkill } from "./createSkill";
import { createUserGroup } from "./createUserGroup";
import { createWorkflow } from "./createWorkflow";
import { deactivateUser } from "./deactivateUser";
import { deleteAIConfig } from "./deleteAIConfig";
import { deleteApiKey } from "./deleteApiKey";
import { deleteAuditLogDestination } from "./deleteAuditLogDestination";
import { deleteBranch } from "./deleteBranch";
import { deleteConnectedApp } from "./deleteConnectedApp";
import { deleteEgressRoute } from "./deleteEgressRoute";
import { deleteFeatureFlag } from "./deleteFeatureFlag";
import { deleteGitSkillSource } from "./deleteGitSkillSource";
import { deleteInvitedUser } from "./deleteInvitedUser";
import { deleteEgressRule } from "./deleteEgressRule";
import { deleteNetwork } from "./deleteNetwork";
import { deletePersonalChat } from "./deletePersonalChat";
import { restorePersonalChat } from "./restorePersonalChat";
import { deleteRole } from "./deleteRole";
import { deleteSkill } from "./deleteSkill";
import { deleteSkillFile } from "./deleteSkillFile";
import { deleteServiceAccount } from "./deleteServiceAccount";
import { deleteServiceAccountFederation } from "./deleteServiceAccountFederation";
import { deleteOidcProvider } from "./deleteOidcProvider";
import { deleteSpace } from "./deleteSpace";
import { deleteSSOProvider } from "./deleteSSOProvider";
import { deleteUsageBudget } from "./deleteUsageBudget";
import { deleteUsagePrice } from "./deleteUsagePrice";
import { deleteUserGroup } from "./deleteUserGroup";
import { deleteWorkflow } from "./deleteWorkflow";
import { deriveWorkflowFromChat } from "./deriveWorkflowFromChat";
import { disableFeatureFlag } from "./disableFeatureFlag";
import { disconnectGitSync } from "./disconnectGitSync";
import { dissociatePersonalChatFromConnectedApp } from "./dissociatePersonalChatFromConnectedApp";
import { dissociateBranchChatFromConnectedApp } from "./dissociateBranchChatFromConnectedApp";
import { dissociateStepFromConnectedApp } from "./dissociateStepFromConnectedApp";
import { enableFeatureFlag } from "./enableFeatureFlag";
import { exportPersonalChat } from "./exportPersonalChat";
import { fetchConnectorCertificate } from "./fetchConnectorCertificate";
import { forkPersonalChat } from "./forkPersonalChat";
import { generateScimToken } from "./generateScimToken";
import { importWorkflow } from "./importWorkflow";
import { inspectGitSkillSource } from "./inspectGitSkillSource";
import { installGitSkills } from "./installGitSkills";
import { stopSubAgent } from "./stopSubAgent";
import { interruptChat } from "./interruptChat";
import { interruptPersonalChat } from "./interruptPersonalChat";
import { inviteOrReactivateUser } from "./inviteOrReactivateUser";
import { joinSpace } from "./joinSpace";
import { markMonitoringTabViewed } from "./markMonitoringTabViewed";
import { markRouteViewed } from "./markRouteViewed";
import { markSpaceTabViewed } from "./markSpaceTabViewed";
import { markWorkflowViewed } from "./markWorkflowViewed";
import { mergeMainIntoBranch } from "./mergeMainIntoBranch";
import { moveWorkflow } from "./moveWorkflow";
import { type AppMutation, traceMutators } from "./mutator";
import { permanentlyDeleteWorkflow } from "./permanentlyDeleteWorkflow";
import { pauseWorkflow } from "./pauseWorkflow";
import { patchScopeGrants } from "./patchScopeGrants";
import { pinStepTest } from "./pinStepTest";
import { renameStepTest } from "./renameStepTest";
import { unpinStepTest } from "./unpinStepTest";
import {
  startConnectorAuthorization,
  completeConnectorAuthorization,
} from "./connectorAuthorization";
import { prepareConnectorOAuthClient } from "./prepareConnectorOAuthClient";
import { providePersonalChatToolResult } from "./providePersonalChatToolResult";
import { provideSpaceChatToolResult } from "./provideSpaceChatToolResult";
import { publishConnectedApp } from "./publishConnectedApp";
import { publishSkill } from "./publishSkill";
import { pushBranchToLive } from "./pushBranchToLive";
import { requestWorkflowChangeReview } from "./requestWorkflowChangeReview";
import { reactivateUser } from "./reactivateUser";
import { resendUserInvite } from "./resendUserInvite";
import { resetBranchVolumeData } from "./resetBranchVolumeData";
import {
  deleteBranchVolumeData,
  deleteLiveVolumeData,
  deletePersonalVolumeData,
} from "./deleteVolumeData";
import { deleteWorkflowRunVolumeData } from "./deleteWorkflowRunVolumeData";
import { recordDemoRequest } from "./recordDemoRequest";
import { recordGalleryWorkflowImported } from "./recordGalleryWorkflowImported";
import { recordMonitoringInteraction } from "./recordMonitoringInteraction";
import { recordChatComposerControl } from "./recordChatComposerControl";
import { recordLinkCopied } from "./recordLinkCopied";
import { recordListControlChange } from "./recordListControlChange";
import { recordNavigationClick } from "./recordNavigationClick";
import { recordPostSignupFormView } from "./recordPostSignupFormView";
import { recoverWorkflow } from "./recoverWorkflow";
import { resyncSkill } from "./resyncSkill";
import { revealRunData } from "./revealRunData";
import { revokeConnectorGrant } from "./revokeConnectorGrant";
import { revokeOwnConnectorGrant } from "./revokeOwnConnectorGrant";
import { refreshConnectorList } from "./refreshConnectorList";
import { restoreWorkflowOnBranch } from "./restoreWorkflowOnBranch";
import { reopenPersonalChat } from "./reopenPersonalChat";
import { reorderPersonalChat } from "./reorderPersonalChat";
import { reviewDuplicatePair } from "./reviewDuplicatePair";
import { refreshDuplicateDetection } from "./refreshDuplicateDetection";
import { reEmitRetainedExecution } from "./reEmitExecution";
import { revokeOAuthGrant } from "./revokeOAuthGrant";
import { registerOidcProvider } from "./registerOidcProvider";
import { registerSSOProvider } from "./registerSSOProvider";
import { removeCustomAvatar } from "./removeCustomAvatar";
import { recordGithubAppInstallation } from "./recordGithubAppInstallation";
import { renameBranch } from "./renameBranch";
import { renamePersonalChat } from "./renamePersonalChat";
import { renameWorkflow } from "./renameWorkflow";
import { resetOAuthConnection } from "./resetOAuthConnection";
import { dismissConnectionClaim } from "./dismissConnectionClaim";
import { resolveConnectionClaim } from "./resolveConnectionClaim";
import { approveGrantRequest } from "./approveGrantRequest";
import { cancelGrantRequest } from "./cancelGrantRequest";
import { dismissGrantRequest } from "./dismissGrantRequest";
import { requestPushLive } from "./requestPushLive";
import { cancelPushLiveRequest } from "./cancelPushLiveRequest";
import { declinePushLiveRequest } from "./declinePushLiveRequest";
import { approvePushLiveRequest } from "./approvePushLiveRequest";
import { requestScopeRoleGrants } from "./requestScopeRoleGrants";
import { restoreToCommit } from "./restoreToCommit";
import { revokeAllSessions } from "./revokeAllSessions";
import { revokeScimToken } from "./revokeScimToken";
import { runStep } from "./runStep";
import { runStepTests } from "./runStepTests";
import { saveToHistory } from "./saveToHistory";
import { seedBranchVolumes } from "./seedBranchVolumes";
import { sendMessage } from "./sendMessage";
import {
  sendLiveChatMessage,
  clearLiveChat,
  interruptLiveChat,
  answerLiveChatBranchRequest,
} from "./liveChat";
import { sendPersonalChatMessage } from "./sendPersonalChatMessage";
import { setConnectorAuthorizationMode } from "./setConnectorAuthorizationMode";
import { setSidebarCollapsed } from "./setSidebarCollapsed";
import { setConnectedAppPersonalUse } from "./setConnectedAppPersonalUse";
import { setWorkflowRetention } from "./setWorkflowRetention";
import { setBranchModelPreference } from "./setBranchModelPreference";
import { setWorkflowTimeSaved } from "./setWorkflowTimeSaved";
import { setCustomAvatar } from "./setCustomAvatar";
import { setGroupGrants } from "./setGroupGrants";
import { setSkillPersonalUse } from "./setSkillPersonalUse";
import { setAIProviderDisabled } from "./setAIProviderDisabled";
import { setManagedAIProviderConfig } from "./setManagedAIProviderConfig";
import { setManagedAIProviderDisabled } from "./setManagedAIProviderDisabled";
import { setServiceAccountTenantRoles } from "./setServiceAccountTenantRoles";
import { setTenantAdmins } from "./setTenantAdmins";
import { setUserGroupTenantRoles } from "./setUserGroupTenantRoles";
import { setPersonalChatPinned } from "./setPersonalChatPinned";
import { setChatFocus } from "./setChatFocus";
import { setPersonalWorkAccess } from "./setPersonalWorkAccess";
import { setCompanyName } from "./setCompanyName";
import { setRouteAuthPolicy } from "./setRouteAuthPolicy";
import { setUserPreference } from "./setUserPreference";
import { setWorkflowAppBacking } from "./setWorkflowAppBacking";
import { setWorkflowConnectorEnabled } from "./setWorkflowConnectorEnabled";
import { switchConnectorVariant } from "./switchConnectorVariant";
import { syncGit } from "./syncGit";
import { testAuditLogDestination } from "./testAuditLogDestination";
import { testConnectedApp } from "./testConnectedApp";
import { tidyUpFreshConnectedApp } from "./tidyUpFreshConnectedApp";
import { toggleAutofix } from "./toggleAutofix";
import { toggleAutoImprove } from "./toggleAutoImprove";
import { toggleRouteFavorite } from "./toggleRouteFavorite";
import { toggleStepRoutePin } from "./toggleStepRoutePin";
import { toggleWorkflowFavorite } from "./toggleWorkflowFavorite";
import { touchPersonalChat } from "./touchPersonalChat";
import { touchPresence } from "./touchPresence";
import { unpauseWorkflow } from "./unpauseWorkflow";
import { updateAIConfig } from "./updateAIConfig";
import { updateAIBudget } from "./updateAIBudget";
import { updateAuditLogDestination } from "./updateAuditLogDestination";
import { updateConnectedApp } from "./updateConnectedApp";
import { updateNetwork } from "./updateNetwork";
import { updateEgressRoute } from "./updateEgressRoute";
import { updateEgressRule } from "./updateEgressRule";
import { updateOAuthConnection } from "./updateOAuthConnection";
import { updateOidcProvider } from "./updateOidcProvider";
import { updateServiceAccount } from "./updateServiceAccount";
import { updateSpace } from "./updateSpace";
import { updateSSOProvider } from "./updateSSOProvider";
import { updateStepColor } from "./updateStepColor";
import { updateStepRouteType } from "./updateStepRouteType";
import { updateSkill } from "./updateSkill";
import { upsertSkillFile } from "./upsertSkillFile";
import { upsertSkillFiles } from "./upsertSkillFiles";
import { updateUsageBudget } from "./updateUsageBudget";
import { updateWorkflowLink } from "./updateWorkflowLink";
import { updateUsagePrice } from "./updateUsagePrice";
import { updateUserGroup } from "./updateUserGroup";
import { updateWorkflowBadge } from "./updateWorkflowBadge";

const definitions = {
  addCustomDomain,
  removeCustomDomain,
  updateCustomDomain,
  checkSkillUpdates,
  configureSkillUpdates,
  applySkillUpdate,
  updateSkillSettings,
  associateBranchChatWithConnectedApp,
  associatePersonalChatWithConnectedApp,
  associateStepWithConnectedApp,
  closePersonalChat,
  deletePersonalChat,
  restorePersonalChat,
  completeProfile,
  configureGitSync,
  createApiKey,
  createAuditLogDestination,
  createConnectedApp,
  createEgressRoute,
  createEgressRule,
  createFeatureFlag,
  createNetwork,
  createPersonalChat,
  createRole,
  createSkill,
  createBranch,
  createServiceAccount,
  createServiceAccountFederation,
  createServiceAccountSession,
  createUserApiKey,
  deleteServiceAccountFederation,
  setServiceAccountTenantRoles,
  setSidebarCollapsed,
  registerOidcProvider,
  updateOidcProvider,
  deleteOidcProvider,
  revokeOAuthGrant,
  createSpace,
  createUserGroup,
  createWorkflow,
  deactivateUser,
  deleteAIConfig,
  deleteAuditLogDestination,
  inviteOrReactivateUser,
  deleteBranch,
  deleteConnectedApp,
  deleteEgressRoute,
  deleteEgressRule,
  deleteFeatureFlag,
  deleteGitSkillSource,
  deleteInvitedUser,
  deleteNetwork,
  deleteRole,
  deleteUsageBudget,
  deleteUsagePrice,
  deleteUserGroup,
  disableFeatureFlag,
  disconnectGitSync,
  deleteSSOProvider,
  deleteServiceAccount,
  deleteSkill,
  deleteSkillFile,
  deleteSpace,
  deleteWorkflow,
  deriveWorkflowFromChat,
  dissociatePersonalChatFromConnectedApp,
  dissociateBranchChatFromConnectedApp,
  dissociateStepFromConnectedApp,
  enableFeatureFlag,
  exportPersonalChat,
  forkPersonalChat,
  generateScimToken,
  importWorkflow,
  inspectGitSkillSource,
  installGitSkills,
  interruptChat,
  stopSubAgent,
  interruptPersonalChat,
  joinSpace,
  markMonitoringTabViewed,
  markRouteViewed,
  markSpaceTabViewed,
  markWorkflowViewed,
  mergeMainIntoBranch,
  moveWorkflow,
  patchScopeGrants,
  pauseWorkflow,
  permanentlyDeleteWorkflow,
  pinStepTest,
  prepareConnectorOAuthClient,
  startConnectorAuthorization,
  completeConnectorAuthorization,
  providePersonalChatToolResult,
  provideSpaceChatToolResult,
  publishConnectedApp,
  publishSkill,
  pushBranchToLive,
  requestWorkflowChangeReview,
  reactivateUser,
  resendUserInvite,
  resetBranchVolumeData,
  deleteLiveVolumeData,
  deleteBranchVolumeData,
  deletePersonalVolumeData,
  deleteWorkflowRunVolumeData,
  recordDemoRequest,
  recordGalleryWorkflowImported,
  recordMonitoringInteraction,
  recordNavigationClick,
  recordLinkCopied,
  recordChatComposerControl,
  recordListControlChange,
  recordPostSignupFormView,
  recoverWorkflow,
  renameStepTest,
  resyncSkill,
  revealRunData,
  revokeConnectorGrant,
  revokeOwnConnectorGrant,
  refreshConnectorList,
  restoreWorkflowOnBranch,
  reopenPersonalChat,
  reorderPersonalChat,
  reviewDuplicatePair,
  refreshDuplicateDetection,
  registerSSOProvider,
  reEmitRetainedExecution,
  removeCustomAvatar,
  renameBranch,
  renamePersonalChat,
  renameWorkflow,
  resetOAuthConnection,
  resolveConnectionClaim,
  dismissConnectionClaim,
  approveGrantRequest,
  cancelGrantRequest,
  dismissGrantRequest,
  requestPushLive,
  cancelPushLiveRequest,
  declinePushLiveRequest,
  approvePushLiveRequest,
  requestScopeRoleGrants,
  recordGithubAppInstallation,
  restoreToCommit,
  revokeAllSessions,
  revokeScimToken,
  deleteApiKey,
  runStep,
  runStepTests,
  saveToHistory,
  seedBranchVolumes,
  sendMessage,
  sendLiveChatMessage,
  clearLiveChat,
  interruptLiveChat,
  answerLiveChatBranchRequest,
  sendPersonalChatMessage,
  setConnectorAuthorizationMode,
  setConnectedAppPersonalUse,
  setSkillPersonalUse,
  setCustomAvatar,
  setGroupGrants,
  setAIProviderDisabled,
  setManagedAIProviderConfig,
  setManagedAIProviderDisabled,
  setTenantAdmins,
  setUserGroupTenantRoles,
  setPersonalChatPinned,
  setChatFocus,
  setPersonalWorkAccess,
  setCompanyName,
  setRouteAuthPolicy,
  setUserPreference,
  setBranchModelPreference,
  setWorkflowRetention,
  setWorkflowTimeSaved,
  setWorkflowAppBacking,
  setWorkflowConnectorEnabled,
  switchConnectorVariant,
  syncGit,
  testAuditLogDestination,
  fetchConnectorCertificate,
  testConnectedApp,
  tidyUpFreshConnectedApp,
  toggleAutofix,
  toggleAutoImprove,
  toggleRouteFavorite,
  toggleStepRoutePin,
  toggleWorkflowFavorite,
  touchPersonalChat,
  touchPresence,
  unpauseWorkflow,
  unpinStepTest,
  updateAIConfig,
  updateAIBudget,
  updateAuditLogDestination,
  updateStepColor,
  updateStepRouteType,
  updateConnectedApp,
  updateNetwork,
  updateEgressRoute,
  updateEgressRule,
  updateServiceAccount,
  updateSSOProvider,
  updateSkill,
  upsertSkillFile,
  upsertSkillFiles,
  updateOAuthConnection,
  updateSpace,
  updateUsageBudget,
  updateWorkflowLink,
  updateUsagePrice,
  updateUserGroup,
  updateWorkflowBadge,
} satisfies Record<string, AppMutation>;

export { makeBranchParams } from "./utils";
export type { AppMutation } from "./mutator";
export const sharedMutators = defineMutators(traceMutators(definitions));
// The registry above erases the `resolve` / `permissions` props from its
// types; server mutators reference the shared declarations through this.
export const sharedMutatorDefs = definitions;
export type MutatorName = keyof typeof definitions;
